/**
 * @file Proactive browser-event token refresh.
 *
 * Listens for configurable browser events (default: `visibilitychange`,
 * `pageshow`, `online`) and refreshes SLAS tokens *before* the user triggers
 * an API call. This
 * eliminates the refresh-latency penalty that returning inactive users would
 * otherwise pay on their first interaction.
 *
 * Shares the same `refreshPromise` and cross-tab lock as the Axios request
 * interceptor — if one fires first the other awaits it, and vice versa.
 *
 * @see core.ts  — shared state, cookie check, cross-tab lock
 * @see axios-interceptor.ts — per-request refresh gate
 */

import { isNullish } from '@/utils/null-utils';
import { Time } from '@/utils/time-utils';
import { needsRefresh, ensureRefresh, getRefreshPromise } from './core';
import { isBrowser } from './utils';
import { TOKEN_REFRESH_CONFIG } from './config';
import type { ITokenRefreshEventsOptions } from './config';

export type { ITokenRefreshEventsOptions } from './config';

const {
  lockKey: LOCK_KEY,
  cookieRegex: REFRESH_AT_REGEX,
  timerBufferSeconds: TIMER_BUFFER_S,
  eventDefaults: EVENT_DEFAULTS
} = TOKEN_REFRESH_CONFIG;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Starts listening for browser events and proactively refreshes tokens when
 * the user returns to the tab/page.
 *
 * The returned cleanup function removes all listeners — suitable for use as
 * the return value of a React `useEffect`.
 *
 * @param options - Optional configuration overrides.
 * @returns A teardown function that removes all event listeners.
 *
 * @example
 * // Plain JS — call once at app startup:
 * const stop = startTokenRefreshEvents();
 *
 * // React — inside a top-level client component:
 * useEffect(() => startTokenRefreshEvents(), []);
 */
export function startTokenRefreshEvents(
  options?: ITokenRefreshEventsOptions
): () => void {
  if (!isBrowser()) return () => {};

  const { events, debounceMs, refreshUrl } = { ...EVENT_DEFAULTS, ...options };
  let lastAttempt = 0;

  const handler = (e: Event): void => {
    // For visibilitychange, only act when the tab becomes visible.
    // Other events (pageshow, online, etc.) fire regardless of visibility.
    if (
      e.type === 'visibilitychange' &&
      document.visibilityState === 'hidden'
    ) {
      return;
    }

    // Debounce: skip if we recently attempted a refresh.
    const now = Date.now();
    if (now - lastAttempt < debounceMs) return;
    lastAttempt = now;

    // Skip if a refresh is already in flight (from the interceptor or a
    // previous event firing).
    if (getRefreshPromise()) return;

    if (needsRefresh()) {
      // Fire-and-forget: the shared `refreshPromise` prevents duplicate calls.
      //
      // eslint-disable-next-line no-void -- See above.
      void ensureRefresh(refreshUrl);
    }
  };

  // When another tab releases the cross-tab lock we re-check freshness. If
  // the cookie was updated we're done; if not (e.g. the other tab crashed)
  // this tab takes over.
  //
  // Note: The `storage` event only fires in *other* documents (tabs/windows),
  // not in the document that triggered the change. This is by design — the
  // originating tab already knows it released the lock.
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
  const storageHandler = (e: StorageEvent): void => {
    if (e.key !== LOCK_KEY || e.newValue !== null) return;

    if (needsRefresh() && !getRefreshPromise()) {
      // Fire-and-forget: if the other tab's refresh didn't update the
      // cookie (e.g. it crashed), this tab takes over.
      //
      // eslint-disable-next-line no-void -- See above.
      void ensureRefresh(refreshUrl);
    }
  };

  for (const event of events) {
    window.addEventListener(event, handler);
  }
  window.addEventListener('storage', storageHandler);

  // Schedule a proactive refresh 1 minute before the refresh_at timestamp so
  // idle users have fresh tokens when they next interact. The timer is
  // window-scoped — it survives React unmounts — and ensureRefresh deduplicates
  // so a stale firing is harmless. After each refresh, reschedule for the new
  // expiry so long-lived sessions stay covered.
  const scheduleProactiveRefresh = (): void => {
    const match = REFRESH_AT_REGEX.exec(document.cookie);
    if (isNullish(match)) return;

    const refreshAtMs = Time.seconds(Number(match[1])).milliseconds;
    const bufferMs = Time.seconds(TIMER_BUFFER_S).milliseconds;
    const delay = refreshAtMs - bufferMs - Date.now();

    if (delay > 0) {
      setTimeout(() => {
        if (needsRefresh()) {
          /* eslint-disable promise/catch-or-return, promise/prefer-await-to-then --
             Fire-and-forget inside setTimeout; both handlers reschedule. */
          ensureRefresh(refreshUrl).then(
            scheduleProactiveRefresh,
            scheduleProactiveRefresh
          );
          /* eslint-enable promise/catch-or-return, promise/prefer-await-to-then */
        } else {
          // Token was refreshed by another mechanism; reschedule for the new expiry.
          scheduleProactiveRefresh();
        }
      }, delay);
    }
  };

  scheduleProactiveRefresh();

  return () => {
    for (const event of events) {
      window.removeEventListener(event, handler);
    }
    window.removeEventListener('storage', storageHandler);
  };
}
